import React, { useRef, useState, useEffect } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useDateSelection } from "../../contexts/DateSelectionProvider";
import { useTranslation } from "react-i18next";

const ReportDate = ({ handleDate }) => {
  const { dates } = useDateSelection();
  const containerRef = useRef(null);
  const { t } = useTranslation();
  
  // Track selected dates - can have 0, 1, or 2 selections
  const [selectedDates, setSelectedDates] = useState([]);

// Clear selections when component mounts
useEffect(() => {
  setSelectedDates([]);
  localStorage.removeItem("date");
  // Notify parent that no date is selected
  handleDate(null, null);
}, []);

  // Handle clicking on a date
  const handleDateClick = (date) => {
    setSelectedDates(prev => {
      // If date is already selected, remove it
      if (prev.includes(date)) {
        return prev.filter(d => d !== date);
      }
      
      // If we have less than 2 dates selected, add the new one
      if (prev.length < 2) {
        return [...prev, date];
      }
      
      // If we already have 2 dates, replace the oldest one
      const sorted = [...prev].sort((a, b) => new Date(a) - new Date(b));
      sorted.shift(); // Remove the oldest
      return [...sorted, date];
    });
  };

// Pass the selected dates to parent when selection changes
useEffect(() => {
  if (selectedDates.length > 0) {
    const sortedDates = [...selectedDates].sort((a, b) => new Date(a) - new Date(b));
    
    if (selectedDates.length === 1) {
      // Only one date selected - no previous date
      handleDate(sortedDates[0], null);
    } else if (selectedDates.length === 2) {
      // Two dates selected - earlier date becomes previous, later date becomes active
      handleDate(sortedDates[1], sortedDates[0]);
    }
  } else {
    // No dates selected
    handleDate(null, null);
  }
}, [selectedDates, handleDate]);

  // Function to handle the click on the arrow (Next dates)
  const handleNext = () => {
    containerRef.current.scrollBy({
      left: containerRef.current.clientWidth,
      behavior: "smooth",
    });
  };

  // Function to handle the click on the arrow (Previous dates)
  const handlePrev = () => {
    containerRef.current.scrollBy({
      left: -containerRef.current.clientWidth,
      behavior: "smooth",
    });
  };

  // Sort dates from the oldest to the latest
  const sortedDates = dates.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Get color class based on selection order
  const getDateColorClass = (date) => {
    if (!selectedDates.includes(date)) return "bg-white hover:bg-gray-100";
    
    const index = selectedDates.indexOf(date);
    if (index === 0) {
      return "bg-green-500 text-white"; // First selected date - green
    } else {
      return "bg-blue-500 text-white"; // Second selected date - blue
    }
  };

  return (
    <div className="flex justify-between overflow-x-hidden items-center w-full">
      {dates.length > 0 ? (
        <button
          onClick={handlePrev}
          className="arrow-btn tooltip"
          data-tip="Previous Dates"
        >
          <FaArrowLeft className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
        </button>
      ) : (
        <button
          className="arrow-btn tooltip"
          data-tip="No previous dates available"
          disabled
        >
          <FaArrowLeft className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
        </button>
      )}

      {dates.length > 0 ? (
        <div
          ref={containerRef}
          className={`flex overflow-x-scroll scroll-smooth scrollbar-hide mx-4`}
        >
          {sortedDates.map((match, index) => (
            <div
              key={index}
              onClick={() => handleDateClick(match.date)}
              className={`flex-shrink-0 p-2 text-sm sm:text-[16px] mx-1 cursor-pointer font-medium text-black rounded-md border border-gray-300
                ${getDateColorClass(match.date)}`}
            >
              {match.date}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-black font-medium text-sm mx-1">{t("No dates available")}</p>
      )}

      {dates.length > 0 ? (
        <button
          onClick={handleNext}
          className="arrow-btn tooltip"
          data-tip="Next Dates"
        >
          <FaArrowRight className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
        </button>
      ) : (
        <button
          className="arrow-btn tooltip"
          data-tip="No next dates available"
          disabled
        >
          <FaArrowRight className="text-sm sm:text-[16px] lg:text-lg 2xl:text-2xl" />
        </button>
      )}
    </div>
  );
};

export default ReportDate;